import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import {
  eventLabel,
  playerDisplayName,
  playerShirtNumber,
  resolveYellowCardEventId,
  YELLOW_CARD_EVENT_ID,
  SECOND_YELLOW_CARD_EVENT_ID,
} from '../utilities/matchesApi';

const QUICK_TYPES = [
  { id: 1, needsAssist: true, tone: 'goal' },
  { id: 6, tone: 'goal' },
  { id: 8, tone: 'own' },
  { id: 3, tone: 'yc' },
  { id: 4, tone: 'rc' },
];

/**
 * Быстрое меню события по клику на игрока в консоли табло.
 * Гол → шаг выбора ассистента (или без паса).
 */
export default function TabloQuickPlayerMenu({
  player,
  teammates = [],
  events = [],
  minute = 0,
  second = 0,
  busy = false,
  t,
  onClose,
  onSubmit,
}) {
  const [step, setStep] = useState('type'); // type | assist
  const [pendingGoal, setPendingGoal] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const title = useMemo(() => {
    if (!player) return '';
    const num = playerShirtNumber(player);
    const name = playerDisplayName(player);
    return num ? `#${num} ${name}` : name;
  }, [player]);

  const clockLabel = `${minute}'${String(second).padStart(2, '0')}`;

  const assistCandidates = useMemo(
    () => teammates.filter((item) => Number(item.id) !== Number(player?.id)),
    [teammates, player]
  );

  const submit = async (eventId, assistId = null) => {
    if (!player || busy) return;
    const resolved = resolveYellowCardEventId(eventId, events, player.id, { isEdit: false });
    await onSubmit({
      eventId: resolved,
      playerId: Number(player.id),
      teamId: Number(player.team_id),
      assistId: assistId ? Number(assistId) : null,
      minute,
      second,
      wasSecondYellow: resolved === SECOND_YELLOW_CARD_EVENT_ID
        && Number(eventId) === YELLOW_CARD_EVENT_ID,
    });
  };

  const pickType = (item) => {
    if (item.needsAssist) {
      setPendingGoal(true);
      setStep('assist');
      return;
    }
    submit(item.id);
  };

  if (!player) return null;

  const menu = (
    <div
      className="tablo-quick-menu"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="tablo-quick-menu-backdrop" aria-hidden="true" />
      <div className="tablo-quick-menu-card" onClick={(e) => e.stopPropagation()}>
        <header className="tablo-quick-menu-head">
          <div>
            <strong>{title}</strong>
            <em>{t('tabloQuickAt')} {clockLabel}</em>
          </div>
          <button type="button" className="tablo-quick-menu-close" onClick={onClose} aria-label={t('cancel')}>
            <FiX />
          </button>
        </header>

        <div className="tablo-quick-menu-body">
          {step === 'type' ? (
            <div className="tablo-quick-menu-grid">
              {QUICK_TYPES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`tablo-quick-menu-btn is-${item.tone}`}
                  disabled={busy}
                  onClick={() => pickType(item)}
                >
                  <span className="tablo-quick-menu-label">{eventLabel(item.id, t)}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="tablo-quick-assist">
              <p className="tablo-quick-assist-title">{t('tabloQuickAssistTitle')}</p>
              <button
                type="button"
                className="tablo-quick-menu-btn is-ghost"
                disabled={busy || !pendingGoal}
                onClick={() => submit(1, null)}
              >
                {t('tabloQuickNoAssist')}
              </button>
              <div className="tablo-quick-assist-list">
                {assistCandidates.map((mate) => {
                  const num = playerShirtNumber(mate);
                  return (
                    <button
                      key={mate.id}
                      type="button"
                      className="tablo-quick-assist-chip"
                      disabled={busy}
                      onClick={() => submit(1, mate.id)}
                    >
                      {num ? <span className="tablo-quick-assist-num">{num}</span> : null}
                      <span>{playerDisplayName(mate)}</span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="st-btn st-btn-ghost tablo-quick-back"
                disabled={busy}
                onClick={() => { setStep('type'); setPendingGoal(false); }}
              >
                {t('cancel')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) return menu;
  return createPortal(menu, document.body);
}
